import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";
import type {
  ErsSession,
  ErsDashboard,
  ErsTemplate,
  CreateErsSessionRequest,
} from "../types/ers.types";

const ersQueryKeys = {
  all: () => ["ers"] as const,
  sessions: (leadId: string) => [...ersQueryKeys.all(), "sessions", leadId] as const,
  session: (sessionId: string) => [...ersQueryKeys.all(), "session", sessionId] as const,
  dashboard: () => [...ersQueryKeys.all(), "dashboard"] as const,
  templates: () => [...ersQueryKeys.all(), "templates"] as const,
};

// Fetch all ERS sessions for a lead
export const useErsSessions = (leadId: string) => {
  return useQuery({
    queryKey: ersQueryKeys.sessions(leadId),
    queryFn: async () => {
      const result = await handleApiCall<ErsSession[]>(async () =>
        api.get({ url: getApiUrl(endpoint.ers.getSessionsByLead(leadId)), auth: true })
      );
      return Array.isArray(result) ? result : [];
    },
    enabled: !!leadId,
    staleTime: 15 * 1000,
  });
};

// Fetch ERS templates
export const useErsTemplates = () => {
  return useQuery({
    queryKey: ersQueryKeys.templates(),
    queryFn: async () => {
      const result = await handleApiCall<ErsTemplate[]>(async () =>
        api.get({ url: getApiUrl(endpoint.ers.getTemplates), auth: true })
      );
      return Array.isArray(result) ? result : [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create ERS session with contacts
export const useCreateErsSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateErsSessionRequest) => {
      return handleApiCall(async () =>
        api.post({ url: getApiUrl(endpoint.ers.createSession), body: data, auth: true })
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.sessions(variables.leadId) });
      showToast("Referral contacts saved successfully", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to create referral session", "error");
    },
  });
};

// Send SMS messages for a session
export const useSendErsMessages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, leadId }: { sessionId: string; leadId: string }) => {
      return handleApiCall(async () =>
        api.post({ url: getApiUrl(endpoint.ers.sendMessages(sessionId)), body: {}, auth: true })
      );
    },
    onSuccess: (result: any, variables) => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.sessions(variables.leadId) });
      if (result?.failed > 0) {
        showToast(`Sent ${result.sent} messages (${result.failed} failed — check A2P verification)`, "warning");
      } else {
        showToast(`Referral messages sent successfully!`, "success");
      }
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to send referral messages", "error");
    },
  });
};

// Mark an ERS contact as converted
export const useMarkErsConverted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactId, leadId }: { contactId: string; leadId: string }) => {
      return handleApiCall(async () =>
        api.patch({ url: getApiUrl(endpoint.ers.markConverted(contactId)), body: {}, auth: true })
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.sessions(variables.leadId) });
      showToast("Contact marked as converted", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to mark as converted", "error");
    },
  });
};

// Delete an ERS session
export const useDeleteErsSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, leadId }: { sessionId: string; leadId: string }) => {
      return handleApiCall(async () =>
        api.delete({ url: getApiUrl(endpoint.ers.deleteSession(sessionId)), auth: true })
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.sessions(variables.leadId) });
      showToast("Session deleted", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to delete session", "error");
    },
  });
};

// Update (replace contacts on) a draft ERS session
export const useUpdateErsSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      leadId,
      data,
    }: {
      sessionId: string;
      leadId: string;
      data: Omit<CreateErsSessionRequest, "leadId">;
    }) => {
      return handleApiCall(async () =>
        api.patch({ url: getApiUrl(endpoint.ers.updateSession(sessionId)), body: data, auth: true })
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.sessions(variables.leadId) });
      showToast("Draft updated successfully", "success");
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to update draft", "error");
    },
  });
};

// Update an ERS contact
export const useUpdateErsContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contactId,
      data,
    }: {
      contactId: string;
      data: { notes?: string; replyStatus?: string };
    }) => {
      return handleApiCall(async () =>
        api.patch({ url: getApiUrl(endpoint.ers.updateContact(contactId)), body: data, auth: true })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ersQueryKeys.all() });
    },
    onError: (error: any) => {
      showToast(error?.message || "Failed to update contact", "error");
    },
  });
};

// Fetch ERS dashboard stats
export const useErsDashboard = (filters?: {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
}) => {
  return useQuery({
    queryKey: [...ersQueryKeys.dashboard(), filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.page) params.set("page", String(filters.page));
      if (filters?.limit) params.set("limit", String(filters.limit));
      if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.set("dateTo", filters.dateTo);
      const url = `${getApiUrl(endpoint.ers.getDashboard)}${params.toString() ? `?${params}` : ""}`;
      const result = await handleApiCall<ErsDashboard>(async () =>
        api.get({ url, auth: true })
      );
      return result;
    },
    staleTime: 30 * 1000,
  });
};
